import React from 'react'
import {
  Document, Page, Text, View, StyleSheet
} from '@react-pdf/renderer'
import { ClientData } from '../types/ClientData'

const NAVY = '#0f1e35'
const BLUE = '#1e40af'
const WHITE = '#ffffff'
const LIGHT_GREY = '#f1f5f9'
const MID_GREY = '#64748b'
const GREEN = '#166534'

const styles = StyleSheet.create({
  page: {
    backgroundColor: WHITE,
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#1e293b',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: NAVY,
  },
  headerLeft: { flex: 1 },
  headerRight: { alignItems: 'flex-end' },
  firmName: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: NAVY },
  docTitle: { fontSize: 10, color: MID_GREY, marginTop: 2 },
  clientName: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY },
  clientDate: { fontSize: 9, color: MID_GREY, marginTop: 2 },
  section: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: MID_GREY,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  statementBox: {
    backgroundColor: NAVY,
    borderRadius: 6,
    padding: 16,
    marginBottom: 20,
  },
  statementLabel: {
    fontSize: 8,
    color: '#94a3b8',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  statementText: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: WHITE,
    lineHeight: 1.6,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  metricCard: {
    width: '31%',
    backgroundColor: LIGHT_GREY,
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
  },
  metricLabel: { fontSize: 8, color: MID_GREY, marginBottom: 4 },
  metricValue: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: NAVY },
  metricSub: { fontSize: 8, color: MID_GREY, marginTop: 2 },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  actionLabel: { fontSize: 10, color: '#1e293b', flex: 1 },
  actionValue: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: NAVY },
  scenarioRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  scenarioTick: {
    fontSize: 10,
    color: GREEN,
    width: 16,
    marginRight: 4,
  },
  scenarioText: { fontSize: 10, color: '#1e293b', flex: 1 },
  soBox: {
    backgroundColor: '#eff6ff',
    borderRadius: 4,
    padding: 10,
    borderLeftWidth: 3,
    borderLeftColor: BLUE,
  },
  soTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: BLUE, marginBottom: 6 },
  soRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  soLabel: { fontSize: 9, color: MID_GREY },
  soValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#1e293b' },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 8, color: MID_GREY },
  disclaimer: {
    fontSize: 7,
    color: '#94a3b8',
    marginTop: 4,
    lineHeight: 1.4,
  },
})

interface Props {
  data: ClientData
}

export function ProgrammeSummaryPDF({ data: d }: Props) {
  const name = d.clientName || 'Client'
  const freedomAge = d.s3FinalFreedomAge || d.targetFreedomAge
  const doNothingAge = d.voyantDoNothing?.retirementAge || 0
  const yearsGained = doNothingAge > 0 ? doNothingAge - freedomAge : 0

  const totalMonthlyPersonal = (d.pensionMonthly || 0) + (d.isaContribution || 0) + (d.mortgageOP || 0)

  const fmt = (n: number) => `£${n.toLocaleString()}`
  const fmtAge = (n: number) => `Age ${n}`

  const scenarioLabels: Record<string, string> = {
    earlier: `Earlier retirement — target age ${d.s3EarlierRetirementAge}`,
    phased: `Phased structure — from age ${d.s3PhasedStartAge}, ${fmt(d.s3PhasedMonthlyIncome)}/month`,
    oneoff: `${d.s3OneOffDescription || 'One-off spend'} — ${fmt(d.s3OneOffAmount)} at age ${d.s3OneOffAtAge}`,
    lifestyle: `Lifestyle business — ${fmt(d.s3LifestyleBusinessMonthlyIncome)}/month ongoing`,
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.firmName}>Motion Accountancy</Text>
            <Text style={styles.docTitle}>Financial Freedom Programme — Summary</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.clientName}>{name}</Text>
            <Text style={styles.clientDate}>{d.sessionDate || new Date().toLocaleDateString('en-GB')}</Text>
          </View>
        </View>

        {/* Life Design Statement */}
        {d.s3LifeDesignStatement ? (
          <View style={styles.statementBox}>
            <Text style={styles.statementLabel}>Life Design Statement</Text>
            <Text style={styles.statementText}>{d.s3LifeDesignStatement}</Text>
          </View>
        ) : null}

        {/* Programme Metrics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>What Three Sessions Did</Text>
          <View style={styles.metricsGrid}>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Freedom age</Text>
              <Text style={styles.metricValue}>{fmtAge(freedomAge)}</Text>
              <Text style={styles.metricSub}>{yearsGained > 0 ? `${yearsGained} years earlier` : `From do-nothing: ${fmtAge(doNothingAge)}`}</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Monthly personal actions</Text>
              <Text style={styles.metricValue}>{fmt(totalMonthlyPersonal)}/mo</Text>
              <Text style={styles.metricSub}>Committed to plan</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Pot at freedom</Text>
              <Text style={styles.metricValue}>{fmt(d.s3FinalPotAtFreedom || 0)}</Text>
              <Text style={styles.metricSub}>Voyant projection</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Monthly drawdown</Text>
              <Text style={styles.metricValue}>{fmt(d.s3FinalMonthlyDrawdown || 0)}/mo</Text>
              <Text style={styles.metricSub}>At freedom age</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Plan funded to</Text>
              <Text style={styles.metricValue}>{fmtAge(d.s3FinalFundedToAge || 0)}</Text>
              <Text style={styles.metricSub}>At 3% stress test</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Stress test</Text>
              <Text style={styles.metricValue}>{d.s3FinalStressResult === 'funded' ? 'Funded' : 'Review'}</Text>
              <Text style={styles.metricSub}>3% returns scenario</Text>
            </View>
          </View>
        </View>

        {/* Committed Actions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Committed Actions — Standing Orders</Text>
          {(d.pensionMonthly || 0) > 0 && (
            <View style={styles.actionRow}>
              <Text style={styles.actionLabel}>Employer pension contribution</Text>
              <Text style={styles.actionValue}>{fmt(d.pensionMonthly)}/month</Text>
            </View>
          )}
          {(d.isaContribution || 0) > 0 && (
            <View style={styles.actionRow}>
              <Text style={styles.actionLabel}>ISA contribution</Text>
              <Text style={styles.actionValue}>{fmt(d.isaContribution)}/month</Text>
            </View>
          )}
          {(d.mortgageOP || 0) > 0 && (
            <View style={styles.actionRow}>
              <Text style={styles.actionLabel}>Mortgage overpayment</Text>
              <Text style={styles.actionValue}>{fmt(d.mortgageOP)}/month</Text>
            </View>
          )}
        </View>

        {/* Scenarios */}
        {d.s3ScenariosCommitted && d.s3ScenariosCommitted.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Life Design — Confirmed Scenarios</Text>
            {(['earlier', 'phased', 'oneoff', 'lifestyle'] as const).map(key =>
              d.s3ScenariosCommitted.includes(key) ? (
                <View key={key} style={styles.scenarioRow}>
                  <Text style={styles.scenarioTick}>✓</Text>
                  <Text style={styles.scenarioText}>{scenarioLabels[key]}</Text>
                </View>
              ) : null
            )}
            <View style={styles.scenarioRow}>
              <Text style={styles.scenarioTick}>✓</Text>
              <Text style={styles.scenarioText}>Stress test — {d.s3StressTestNote || `Funded to age ${d.s3LongevityFundedToAge}`}</Text>
            </View>
            {d.s3LongevityFundedToAge > 0 && (
              <View style={styles.scenarioRow}>
                <Text style={styles.scenarioTick}>✓</Text>
                <Text style={styles.scenarioText}>Longevity — funded to age {d.s3LongevityFundedToAge}</Text>
              </View>
            )}
          </View>
        )}

        {/* Annual Review */}
        {d.annualReviewDate ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Next Steps</Text>
            <View style={styles.soBox}>
              <Text style={styles.soTitle}>Annual Review Retainer</Text>
              <View style={styles.soRow}>
                <Text style={styles.soLabel}>Annual fee</Text>
                <Text style={styles.soValue}>{fmt(d.retainerFee)} + VAT</Text>
              </View>
              <View style={styles.soRow}>
                <Text style={styles.soLabel}>First annual review</Text>
                <Text style={styles.soValue}>{d.annualReviewDate}</Text>
              </View>
              {d.s3HomeworkAction1 ? (
                <View style={styles.soRow}>
                  <Text style={styles.soLabel}>Action 1</Text>
                  <Text style={styles.soValue}>{d.s3HomeworkAction1}</Text>
                </View>
              ) : null}
              {d.s3HomeworkAction2 ? (
                <View style={styles.soRow}>
                  <Text style={styles.soLabel}>Action 2</Text>
                  <Text style={styles.soValue}>{d.s3HomeworkAction2}</Text>
                </View>
              ) : null}
            </View>
          </View>
        ) : null}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Motion Accountancy Ltd — {name} — Financial Freedom Programme</Text>
          <Text style={styles.footerText}>{new Date().toLocaleDateString('en-GB')}</Text>
        </View>
        <Text style={styles.disclaimer}>
          This document is a summary of decisions made during the Financial Freedom Programme. It does not constitute regulated financial advice. All projections are for planning purposes only and are not guaranteed. Past performance is not indicative of future results. Motion Accountancy Ltd is not authorised to provide regulated financial advice. Please consult a qualified financial adviser for personal recommendations.
        </Text>

      </Page>
    </Document>
  )
}
